import { Text, Section, Box } from '@acast/decibel';
import * as Sentry from '@sentry/browser';
import { create } from 'client/services/api';
import T from 'prop-types';
import React from 'react';
import { presentModal } from 'services/modals';
import AppleDistribution from './apple-distribution';
import renderDistributionErrorModal from './error';
import SpotifyDistribution from './spotify-distribution';

class SettingsDistribution extends React.Component {
  static fetchAvailableIntegrations() {
    return create.get('/api-v3/integrations');
  }

  constructor(props) {
    super(props);
    this.state = {
      error: null,
      integrationStatus: {
        apple: {
          pending: false,
          enabled: false,
          id: '',
          data: {},
        },
      },
      availableIntegrations: ['apple'],
    };
  }

  componentDidMount() {
    this.fetchIntegrations();
  }

  handleOnSubmit = integrationType => {
    const integrationData = this.state.integrationStatus[integrationType].data;
    this.addIntegration(integrationType, integrationData);
  };

  handleOnRemove = integrationType => {
    const integrationId = this.state.integrationStatus[integrationType].id;
    const integrationData = this.state.integrationStatus[integrationType].data;
    this.deleteIntegration(integrationType, integrationId, integrationData);
  };

  async fetchIntegrations() {
    const [availableIntegrationTypes, enabledIntegrations] = await Promise.all([
      SettingsDistribution.fetchAvailableIntegrations(),
      this.fetchEnabledIntegrations(),
    ]);

    let combinedAvailableIntegrationTypes = [];
    const hasAvailableIntegrationTypes =
      availableIntegrationTypes && availableIntegrationTypes.body.items.length > 0;
    const showHasEnabledIntegrations =
      enabledIntegrations && enabledIntegrations.body.items.length > 0;

    if (hasAvailableIntegrationTypes) {
      combinedAvailableIntegrationTypes = this.initIntegrationStatus(availableIntegrationTypes);
    }

    if (showHasEnabledIntegrations) {
      this.updateEnableIntegrationStatus(combinedAvailableIntegrationTypes, enabledIntegrations);
    }
  }

  updateEnableIntegrationStatus = (combinedAvailableIntegrationTypes, enabledIntegrations) => {
    const newIntegrationStatus = {
      ...this.state.integrationStatus,
    };

    enabledIntegrations.body.items.forEach(({ type, id, data }) => {
      if (combinedAvailableIntegrationTypes.includes(type)) {
        newIntegrationStatus[type] = {
          pending: false,
          enabled: true,
          id,
          data,
        };
      }
    });

    this.setState({
      integrationStatus: newIntegrationStatus,
    });
  };

  initIntegrationStatus = availableIntegrationTypes => {
    const combinedAvailableIntegrationTypes = [
      ...availableIntegrationTypes.body.items,
      ...this.state.availableIntegrations,
    ];

    const newIntegrationStatus = {
      ...this.state.integrationStatus,
    };

    combinedAvailableIntegrationTypes.forEach(integrationType => {
      newIntegrationStatus[integrationType] = {
        pending: false,
        enabled: false,
        id: '',
        data: {},
      };
    });

    this.setState({
      integrationStatus: newIntegrationStatus,
      availableIntegrations: combinedAvailableIntegrationTypes,
    });

    return combinedAvailableIntegrationTypes;
  };

  fetchEnabledIntegrations = () => {
    return create.get(`/api-v3/shows/${this.props.show.id}/integrations`);
  };

  updateIntegrationStatus = ({ integrationType, pending, enabled, id = '', data = {} }) => {
    const newIntegrationStatus = {
      ...this.state.integrationStatus,
    };

    newIntegrationStatus[integrationType] = {
      pending,
      enabled,
      id,
      data,
    };

    this.setState({ integrationStatus: newIntegrationStatus });
  };

  presentErrorModel = (err, action, integrationType) => {
    Sentry.captureException(err, { extra: err.response });
    presentModal(
      renderDistributionErrorModal(
        action,
        err.response.status,
        this.props.show.name,
        integrationType
      )
    );
  };

  addIntegration = (integrationType, integrationData) => {
    this.updateIntegrationStatus({
      integrationType,
      pending: true,
      enabled: false,
      data: integrationData,
    });

    create
      .post(`/api-v3/shows/${this.props.show.id}/integrations`, { type: integrationType })
      .then(res => {
        const { type, id, data } = res.body;
        this.updateIntegrationStatus({
          integrationType: type,
          pending: false,
          enabled: true,
          id,
          data,
        });
      })
      .catch(err => {
        this.updateIntegrationStatus({ integrationType, pending: false, enabled: false });
        this.setState({ error: err.response.status });
        this.presentErrorModel(err, 'add', integrationType);
      });
  };

  deleteIntegration = (integrationType, id, data) => {
    this.updateIntegrationStatus({
      integrationType,
      pending: true,
      enabled: true,
      id,
      data,
    });

    create
      .delete(`/api-v3/shows/${this.props.show.id}/integrations/${id}`)
      .then(() => {
        this.updateIntegrationStatus({
          integrationType,
          pending: false,
          enabled: false,
          id,
          data,
        });
      })
      .catch(err => {
        this.updateIntegrationStatus({
          integrationType,
          pending: false,
          enabled: true,
          id,
          data,
        });
        this.setState({ error: err.response.status });
        this.presentErrorModel(err, 'delete', integrationType);
      });
  };

  fetchIntegrationStatus = (integrationType, id, integrationData) => {
    this.updateIntegrationStatus({
      integrationType,
      pending: true,
      enabled: true,
      id,
      integrationData,
    });

    create
      .get(`/api-v3/shows/${this.props.show.id}/integrations/${id}`)
      .then(res => {
        const { data } = res.body;
        this.updateIntegrationStatus({
          integrationType,
          pending: false,
          enabled: true,
          id,
          data,
        });
      })
      .catch(err => {
        this.updateIntegrationStatus({
          integrationType,
          pending: false,
          enabled: true,
          id,
          integrationData,
        });
        this.setState({ error: err.response.status });
        this.presentErrorModel(err, 'get', integrationType);
      });
  };

  renderIntegration = integrationType => {
    if (!this.state.integrationStatus[integrationType]) {
      return null;
    }
    switch (integrationType) {
      case 'spotify':
        return (
          <SpotifyDistribution
            integrationStatus={this.state.integrationStatus[integrationType]}
            deleteIntegration={() =>
              this.deleteIntegration(
                integrationType,
                this.state.integrationStatus[integrationType].id,
                this.state.integrationStatus[integrationType].data
              )
            }
            addIntegration={() =>
              this.addIntegration(
                integrationType,
                this.state.integrationStatus[integrationType].data
              )
            }
            getIntegration={() =>
              this.fetchIntegrationStatus(
                integrationType,
                this.state.integrationStatus[integrationType].id,
                this.state.integrationStatus[integrationType].data
              )
            }
          />
        );
      case 'apple':
        return <AppleDistribution show={this.props.show} />;
      default:
        return null;
    }
  };

  renderIntegrationColumns = () => {
    return this.state.availableIntegrations.map(integrationType =>
      this.renderIntegration(integrationType)
    );
  };

  render() {
    return (
      <Section>
        <Text variant="h3">Distribution</Text>
        <Box gap={6} flexDirection="column">
          {this.renderIntegrationColumns()}
        </Box>
      </Section>
    );
  }
}

SettingsDistribution.propTypes = {
  show: T.object,
};

export default SettingsDistribution;
